import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

/*
 * Nothing displays over a broadcast (2026-10-27): no warning, modal, toast,
 * banner or popover may cover a performer's live video, nor show on the OBS
 * overlay (`/obs/...`).
 *
 * App-level overlays (the photosensitivity warning, ENG-836) cannot know what
 * the page under them shows, so pages tell them here:
 *
 * - `useBroadcastSurface()`: "this page shows a broadcast". Called by every
 *   video player (VideoPlayer, HomeStreamEmbed), the stream page itself (so
 *   it counts while it is still loading, before its player mounts) and the
 *   OBS overlay. While any surface is mounted, app-level overlays must not
 *   cover the page.
 * - `<BroadcastSafeSlot />`: a place on a broadcast page, outside the player,
 *   where app-level content may render inline instead (the stream page's side
 *   column). It never sits over the player.
 *
 * Registration runs in a layout effect, so an overlay hears of a page's
 * surface before the browser paints that page.
 */

const BroadcastSurfaceContext = createContext(null);

const NONE = Object.freeze({ surfaces: 0, slot: null });

export function BroadcastSurfaceProvider({ children }) {
  const [surfaces, setSurfaces] = useState(0);
  const [slot, setSlot] = useState(null);

  const registerSurface = useCallback(() => {
    setSurfaces((n) => n + 1);
    return () => setSurfaces((n) => n - 1);
  }, []);

  const value = useMemo(() => ({ surfaces, slot, registerSurface, setSlot }), [surfaces, slot, registerSurface]);
  return <BroadcastSurfaceContext.Provider value={value}>{children}</BroadcastSurfaceContext.Provider>;
}

/** `{ surfaces, slot }`: how many broadcast surfaces are mounted, and the safe slot's element (or null). */
export const useBroadcastGuard = () => {
  const ctx = useContext(BroadcastSurfaceContext);
  return ctx ? { surfaces: ctx.surfaces, slot: ctx.slot } : NONE;
};

/**
 * Marks the calling component's page as showing a broadcast while it is
 * mounted and `active` (a player passes whether it is rendering a video frame).
 */
export const useBroadcastSurface = (active = true) => {
  const registerSurface = useContext(BroadcastSurfaceContext)?.registerSurface;
  useLayoutEffect(() => (registerSurface && active ? registerSurface() : undefined), [registerSurface, active]);
};

/**
 * Where app-level content renders inline on a broadcast page. `display:
 * contents`, so it adds nothing to the layout until something renders in it.
 */
export const BroadcastSafeSlot = () => {
  const setSlot = useContext(BroadcastSurfaceContext)?.setSlot;
  const ref = useCallback((el) => setSlot?.(el), [setSlot]);
  return <div ref={ref} className="broadcast-safe-slot" data-testid="broadcast-safe-slot" style={{ display: 'contents' }} />;
};
